import { amountInWords } from "@/lib/amountInWords";
import { COMPANY_PDF_LOGO } from "@/lib/brand";
import { formatDate, formatNumber } from "@/lib/format";

// Print payload for the Receipt PDF preview. Mirrors the shape used by
// QuotationPrint / SalesInvoicePrint so the off-screen html2canvas pipeline
// in `web/src/lib/htmlPdf.ts` can render it without bespoke handling.
export interface ReceiptPrintData {
  number: string;
  paymentDate?: string | number | null;
  customerName: string;
  quotationRef?: string;
  paymentMethod?: string;
  totalAmount: number;
  amountPaid: number;
  balance: number;
  notes?: string;
  // Cheque number — present only when the payment method is Cheque.
  chequeNumber?: string | null;
  // Reference-only quotation details captured on general (on-account)
  // payments. Shown for traceability; they do not allocate the payment.
  referenceQuoteNumber?: string | null;
  referenceQuoteAmount?: number | null;
  cancellation?: {
    reason: string;
    cancelledBy: string;
    cancelledAt: string;
  } | null;
}

export interface ReceiptPrintCompany {
  name: string;
  address: string;
  phone: string;
  email: string;
  currency: string;
  currencySymbol: string;
}

interface Props {
  receipt: ReceiptPrintData;
  company: ReceiptPrintCompany;
  preparedBy?: string;
}

export default function ReceiptPrint({
  receipt,
  company,
  preparedBy = "System",
}: Props) {
  const paidAmt = Number(receipt.amountPaid || 0);
  const sym = company.currencySymbol || "GH\u20B5";
  const paidInWords = amountInWords(paidAmt, {
    currencyCode: company.currency,
  });

  return (
    <div
      className="print-area relative bg-white text-slate-900 p-5 font-sans overflow-hidden"
      style={{ width: "800px" }}
    >
      {receipt.cancellation ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-0">
          <div
            className="text-rose-500/15 font-extrabold tracking-widest whitespace-nowrap select-none text-center leading-tight"
            style={{ transform: "rotate(-30deg)", fontSize: "72px" }}
          >
            CANCELLED
          </div>
        </div>
      ) : null}

      <div className="relative z-10">
        <div className="flex justify-between items-start border-b-2 border-slate-300 pb-2">
          <div className="flex items-center gap-4">
            <img
              src={COMPANY_PDF_LOGO}
              alt="Company logo"
              className="h-20 w-auto object-contain"
            />
            <div>
              <div className="text-lg font-bold uppercase leading-tight">
                {company.name}
              </div>
              <div className="text-[11px] mt-0.5">{company.address}</div>
              <div className="text-[11px]">Tel: {company.phone}</div>
              <div className="text-[11px]">Email: {company.email}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold tracking-wide leading-tight">
              RECEIPT
            </div>
            <div className="text-sm font-mono mt-0.5">{receipt.number}</div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-[13px] leading-tight">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Bill To</div>
            <div className="font-semibold mt-0.5">
              {receipt.customerName || "—"}
            </div>
          </div>
          <div className="text-right space-y-0.5">
            <div>
              <span className="text-[10px] uppercase text-slate-500">
                Payment Date:{" "}
              </span>
              <span className="font-semibold">
                {receipt.paymentDate ? formatDate(receipt.paymentDate) : "—"}
              </span>
            </div>
            {receipt.quotationRef ? (
              <div>
                <span className="text-[10px] uppercase text-slate-500">
                  Quotation Ref:{" "}
                </span>
                <span className="font-semibold font-mono">
                  {receipt.quotationRef}
                </span>
              </div>
            ) : null}
            {receipt.paymentMethod ? (
              <div>
                <span className="text-[10px] uppercase text-slate-500">
                  Method:{" "}
                </span>
                <span className="font-semibold">
                  {receipt.paymentMethod}
                  {receipt.chequeNumber && receipt.chequeNumber.trim()
                    ? ` (#${receipt.chequeNumber})`
                    : ""}
                </span>
              </div>
            ) : null}
            {receipt.referenceQuoteNumber &&
            receipt.referenceQuoteNumber.trim() ? (
              <div>
                <span className="text-[10px] uppercase text-slate-500">
                  Ref. Quotation:{" "}
                </span>
                <span className="font-semibold font-mono">
                  {receipt.referenceQuoteNumber}
                </span>
              </div>
            ) : null}
            {receipt.referenceQuoteAmount != null &&
            receipt.referenceQuoteAmount > 0 ? (
              <div>
                <span className="text-[10px] uppercase text-slate-500">
                  Ref. Quote Amount:{" "}
                </span>
                <span className="font-semibold font-mono">
                  {sym} {formatNumber(receipt.referenceQuoteAmount)}
                </span>
              </div>
            ) : null}
          </div>
        </div>

        <table className="w-full mt-3 text-[11px] border-collapse">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-300 px-2 py-1.5 text-left">
                Description
              </th>
              <th className="border border-slate-300 px-2 py-1.5 text-right w-40">
                Amount ({sym})
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-300 px-2 py-1.5 align-middle">
                Payment received against quotation{" "}
                <span className="font-mono">
                  {receipt.quotationRef || "—"}
                </span>
              </td>
              <td className="border border-slate-300 px-2 py-1.5 text-right font-mono align-middle">
                {formatNumber(paidAmt)}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="mt-2 flex justify-end">
          <table className="text-[11px] w-72">
            <tbody>
              <tr className="border-t-2 border-slate-400">
                <td className="py-1 font-bold">Amount Paid ({sym})</td>
                <td className="py-1 text-right font-bold text-sm">
                  {formatNumber(paidAmt)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500">
            Amount in words
          </div>
          <div className="mt-0.5 text-[11px] italic font-bold text-slate-700">
            {paidInWords}
          </div>
        </div>

        <div className="mt-3 pt-2 border-t border-slate-200 text-xs">
          <div>Thank you for your payment!</div>
          {receipt.notes && receipt.notes.trim() ? (
            <div className="mt-1 text-[10px] text-slate-600">
              Notes: {receipt.notes}
            </div>
          ) : null}
          {receipt.cancellation ? (
            <div className="mt-2 pt-2 border-t border-rose-300 text-xs text-rose-700">
              <div className="uppercase font-semibold tracking-wide">
                Cancelled
              </div>
              <div className="mt-1">
                Reason: {receipt.cancellation.reason}
              </div>
              {receipt.cancellation.cancelledBy ? (
                <div>
                  By: {receipt.cancellation.cancelledBy}
                  {receipt.cancellation.cancelledAt
                    ? ` — ${receipt.cancellation.cancelledAt}`
                    : ""}
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="mt-3 pt-2 border-t border-slate-200 flex items-end justify-between">
            <div className="text-[11px]">
              <div className="text-slate-500 uppercase tracking-wide">
                Prepared By
              </div>
              <div className="font-semibold mt-0.5">{preparedBy}</div>
            </div>
            <div className="text-[11px] text-right min-w-[220px]">
              <div className="text-slate-500 uppercase tracking-wide">
                Customer Signature
              </div>
              <div className="mt-4 border-b border-slate-400 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
